import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMessages, getMessage, updateMessage } from '@/api/messages';
import type { UpdateMessageRequest } from '@/types/message';

/**
 * Hook to fetch the list of messages for the current user.
 */
export function useMessages(status: string = 'active') {
  return useQuery({
    queryKey: ['messages', { status }],
    queryFn: () => getMessages({ status }),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch a specific message with full conversation history.
 */
export function useMessage(messageId: string | undefined) {
  return useQuery({
    queryKey: ['messages', messageId],
    queryFn: () => getMessage(messageId!),
    enabled: !!messageId,
    staleTime: 10 * 1000, // 10 seconds
  });
}

/**
 * Hook to update message metadata (mark as read, archive, etc.).
 */
export function useUpdateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, data }: { messageId: string; data: UpdateMessageRequest }) =>
      updateMessage(messageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}
